import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Filter, ArrowRight } from "lucide-react";
import SPShell from "@/components/SPShell";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const FILTERS = [
  { key: "ALL", label: "All", testid: "sp-filter-all" },
  { key: "NEW", label: "New", testid: "sp-filter-new" },
  { key: "VIEWED", label: "Viewed", testid: "sp-filter-viewed" },
  { key: "RESPONDED", label: "Responded", testid: "sp-filter-responded" },
  { key: "QUOTED", label: "Quoted", testid: "sp-filter-quoted" },
  { key: "DECLINED", label: "Declined", testid: "sp-filter-declined" },
  { key: "CLOSED", label: "Closed", testid: "sp-filter-closed" },
];

const STATUS_STYLE = {
  NEW: { bg: "var(--blue-light)", color: "var(--blue-dark)" },
  VIEWED: { bg: "#fef3c7", color: "#92400e" },
  RESPONDED: { bg: "#dcfce7", color: "#166534" },
  QUOTED: { bg: "#dcfce7", color: "#166534" },
  DECLINED: { bg: "#fee2e2", color: "#991b1b" },
  CLOSED: { bg: "#e2e8f0", color: "#475569" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.NEW;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider"
      style={{ background: s.bg, color: s.color }}
    >
      {status}
    </span>
  );
}

export default function SPLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("ALL");

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/sp/leads");
        if (mounted) setLeads(data);
      } catch (e) {
        if (mounted) setError(apiError(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(
    () => (filter === "ALL" ? leads : leads.filter((l) => l.status === filter)),
    [filter, leads]
  );

  return (
    <SPShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8" data-testid="sp-leads-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: "var(--navy)" }} data-testid="sp-leads-heading">
            Leads
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>
            All quote requests and assignments routed to your business.
          </p>
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1" data-testid="sp-leads-filters">
          <Filter size={14} style={{ color: "var(--slate)" }} className="shrink-0" />
          {FILTERS.map((f) => {
            const isActive = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={f.testid}
                className="text-xs px-3 py-1.5 rounded-full border whitespace-nowrap font-medium transition-colors"
                style={{
                  borderColor: isActive ? "var(--blue)" : "var(--border)",
                  background: isActive ? "var(--blue-light)" : "white",
                  color: isActive ? "var(--blue-dark)" : "var(--slate)",
                }}
              >
                {f.label}
                {f.key !== "ALL" && (
                  <span className="ml-1.5 opacity-70">
                    ({leads.filter((l) => l.status === f.key).length})
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {error && (
          <div
            className="rounded-lg p-4 text-sm mb-6"
            style={{ background: "var(--alert-light)", color: "var(--alert)" }}
            data-testid="sp-leads-error"
          >
            {error}
          </div>
        )}

        <div
          className="rounded-xl bg-white border overflow-hidden"
          style={{ borderColor: "var(--border)" }}
        >
          {loading ? (
            <div className="p-5 space-y-3">
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-5 py-16 text-center" data-testid="sp-leads-empty">
              <Inbox size={32} className="mx-auto mb-3" style={{ color: "var(--slate)" }} />
              <p className="text-sm font-medium" style={{ color: "var(--navy)" }}>
                No leads in this view
              </p>
              <p className="text-xs mt-1" style={{ color: "var(--slate)" }}>
                Try switching filter to &ldquo;All&rdquo; or check back later.
              </p>
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--border)" }} data-testid="sp-leads-list">
              {filtered.map((l) => (
                <li key={l.lead_id} data-testid={`sp-lead-row-${l.lead_id}`}>
                  <Link
                    to={`/sp/leads/${l.lead_id}`}
                    className="flex items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold truncate" style={{ color: "var(--navy)" }}>
                          {l.scheme_name || "Scheme"}
                        </span>
                        <StatusPill status={l.status} />
                      </div>
                      <div className="text-xs mt-1" style={{ color: "var(--slate)" }}>
                        {l.source === "insurance_quote_request" ? "Insurance quote request" : l.source}
                        {" · received "}
                        {new Date(l.submitted_at).toLocaleString()}
                        {l.responded_at && (
                          <>
                            {" · responded "}
                            {new Date(l.responded_at).toLocaleDateString()}
                          </>
                        )}
                      </div>
                      {l.response_summary && (
                        <div
                          className="text-xs mt-1 truncate"
                          style={{ color: "var(--slate)" }}
                        >
                          “{l.response_summary.slice(0, 120)}{l.response_summary.length > 120 ? "…" : ""}”
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs shrink-0"
                      data-testid={`sp-open-lead-${l.lead_id}`}
                    >
                      Open
                      <ArrowRight size={12} className="ml-1" />
                    </Button>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </SPShell>
  );
}
